import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Skeleton,
  Typography,
  Result,
  Button,
  Menu,
  Layout,
  Drawer,
  Select,
  Tag,
} from "antd";
import { useHistory, Link } from "react-router-dom";
import { MenuOutlined } from "@ant-design/icons";
import "./MarketplaceOrgDetails.css";
import axios from "axios";
import { useAuth0 } from "../../react-auth0-spa";
import { useAppContext } from "../../context";
import config from "../../api_config.json";
import orgDetailsQuery from "./queries/orgDetailsQuery";

// Import tab components
import MarketplaceOrgRiepilogo from "./tabs/MarketplaceOrgRiepilogo";
import MarketplaceOrgUsers from "./tabs/MarketplaceOrgUsers";
import MarketplaceOrgSettings from "./tabs/MarketplaceOrgSettings";

const { Title } = Typography;
const { Sider, Content } = Layout;
const { Option } = Select;

const MarketplaceOrgDetails = (props) => {
  const { orgId } = props.match.params;
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const history = useHistory();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [openKeys, setOpenKeys] = useState([]);

  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";

  const fetchOrg = async () => {
    setLoading(true);
    try {
      const token = await getTokenSilently();
      const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

      const response = await axios.post(
        graphqlUrl,
        {
          query: orgDetailsQuery,
          variables: { id: orgId },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setOrg(response.data?.marketplace_org);
    } catch (error) {
      console.error("Error fetching org details:", error);
    } finally {
      setLoading(false);
    }
  };

  // Menu items
  const menuItems = useMemo(
    () =>
      org
        ? [
            {
              key: "generale",
              label: "Generale",
              children: [
                { key: "riepilogo", label: "Riepilogo" },
                { key: "users", label: "Utenti" },
              ],
            },
            {
              key: "configurazioni",
              label: "Configurazioni",
              children: [{ key: "settings", label: "Impostazioni" }],
            },
          ]
        : [],
    [org]
  );

  // Flatten menu items for mobile dropdown
  const flatMenuItems = useMemo(() => {
    const items = [];
    menuItems.forEach((group) => {
      if (group.children) {
        group.children.forEach((item) => {
          items.push(item);
        });
      }
    });
    return items;
  }, [menuItems]);

  // Function to check if a tab key is valid
  const isValidTab = useCallback(
    (tabKey) => {
      return flatMenuItems.some((item) => item.key === tabKey);
    },
    [flatMenuItems]
  );

  // Initialize activeTab state
  const initialTab = props.match.params.tabName || "riepilogo";
  const [activeTab, setActiveTab] = useState(initialTab);

  // Find which group contains the active tab and keep it open
  useEffect(() => {
    const activeGroup = menuItems.find((group) => {
      if (group.children) {
        return group.children.some((item) => item.key === activeTab);
      }
      return false;
    });

    if (activeGroup) {
      setOpenKeys([activeGroup.key]);
    }
  }, [activeTab, menuItems]);

  // Redirect to default tab if no tabName is provided
  useEffect(() => {
    if (!props.match.params.tabName) {
      setTimeout(() => {
        history.replace(`/funeralcloud/orgs/${orgId}/riepilogo`);
      }, 0);
    }
  }, [orgId, history, props.match.params.tabName]);

  // Update active tab when URL changes
  useEffect(() => {
    if (!org) return;

    if (props.match.params.tabName && isValidTab(props.match.params.tabName)) {
      setActiveTab(props.match.params.tabName);
    } else {
      setActiveTab("riepilogo");
      if (props.match.params.tabName && !isValidTab(props.match.params.tabName)) {
        setTimeout(() => {
          history.replace(`/funeralcloud/orgs/${orgId}/riepilogo`);
        }, 0);
      }
    }
  }, [props.match.params.tabName, orgId, history, org, isValidTab]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch org data on mount
  useEffect(() => {
    fetchOrg();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  // Handle menu item click
  const handleMenuClick = (e) => {
    setActiveTab(e.key);
    setTimeout(() => {
      history.push(`/funeralcloud/orgs/${orgId}/${e.key}`);
    }, 0);
    if (isMobile) {
      setDrawerVisible(false);
    }
  };

  // Handle dropdown change for mobile
  const handleDropdownChange = (value) => {
    setActiveTab(value);
    setTimeout(() => {
      history.push(`/funeralcloud/orgs/${orgId}/${value}`);
    }, 0);
  };

  // Handle submenu open/close
  const handleOpenChange = (keys) => {
    setOpenKeys(keys);
  };

  if (loading) {
    return <Skeleton active />;
  }

  if (!org) {
    return (
      <div className="marketplace-org-details-container">
        <Result
          status="404"
          title="Organizzazione non trovata"
          subTitle="L'organizzazione FuneralCloud richiesta non esiste."
          extra={
            <Button type="primary">
              <Link to="/funeralcloud/orgs">Torna alla lista</Link>
            </Button>
          }
        />
      </div>
    );
  }

  // Render the active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case "riepilogo":
        return <MarketplaceOrgRiepilogo org={org} />;
      case "users":
        return <MarketplaceOrgUsers orgId={orgId} />;
      case "settings":
        return <MarketplaceOrgSettings org={org} onUpdate={fetchOrg} />;
      default:
        return <MarketplaceOrgRiepilogo org={org} />;
    }
  };

  return (
    <div className="marketplace-org-details-container">
      <div className="marketplace-org-header">
        <div className="marketplace-org-header-title">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Title level={2} style={{ margin: 0 }}>
              {org.name}
            </Title>
            <Tag color={org.is_active ? "green" : "red"} style={{ margin: 0 }}>
              {org.is_active ? "ATTIVO" : "DISATTIVO"}
            </Tag>
          </div>
        </div>

        {isMobile && (
          <div className="mobile-tab-selector">
            <Select
              value={activeTab}
              onChange={handleDropdownChange}
              style={{ width: "100%", marginBottom: "20px" }}
            >
              {flatMenuItems.map((item) => (
                <Option key={item.key} value={item.key}>
                  {item.label}
                </Option>
              ))}
            </Select>
          </div>
        )}
      </div>

      <Layout className="marketplace-org-details-layout">
        {!isMobile ? (
          <Sider width={250} className="marketplace-org-details-sider" theme="light">
            <Menu
              mode="inline"
              selectedKeys={[activeTab]}
              openKeys={openKeys}
              onOpenChange={handleOpenChange}
              onClick={handleMenuClick}
              items={menuItems}
              style={{ height: "100%" }}
            />
          </Sider>
        ) : (
          <div className="mobile-menu-button">
            <Button
              type="primary"
              icon={<MenuOutlined />}
              onClick={() => setDrawerVisible(true)}
              style={{ marginBottom: "16px" }}
            >
              Menu
            </Button>
            <Drawer
              title="Menu"
              placement="left"
              onClose={() => setDrawerVisible(false)}
              open={drawerVisible}
            >
              <Menu
                mode="inline"
                selectedKeys={[activeTab]}
                openKeys={openKeys}
                onOpenChange={handleOpenChange}
                onClick={handleMenuClick}
                items={menuItems}
                style={{ border: "none" }}
              />
            </Drawer>
          </div>
        )}
        <Content className="marketplace-org-details-content">{renderTabContent()}</Content>
      </Layout>
    </div>
  );
};

export default MarketplaceOrgDetails;
